import contentfulClient from '@/lib/services/contentful';
import { unstable_cache } from 'next/cache';
import { cache } from 'react';
import type { TypeProductCatalogue } from '../../types/contentful/TypeProductCatalogue';
import { TypeProductCatalogueSkeleton } from '../../types/contentful/TypeProductCatalogue';
import { TypeProduct__categorySkeleton as TypeProductCategorySkeleton } from '../../types/contentful/TypeProduct__category';
import { REVALIDATION_TIME } from '../constants';
import { getAssetIconData } from '../getImageSrc';

type ProductCatalogueFields = TypeProductCatalogue<'WITHOUT_UNRESOLVABLE_LINKS', string>['fields'];

export const transformProductCatalogueFields = (returnedCatalogue: ProductCatalogueFields) => {
  return {
    ...returnedCatalogue,
    // Feature Table Groups
    featureTableGroups: returnedCatalogue.featureTableGroups
      ?.map((featureGroup) => {
        if (!featureGroup?.fields) return null;
        return {
          title: featureGroup.fields.title,
          features: featureGroup.fields.features
            ?.map((feature) => feature?.fields)
            .filter((feature): feature is NonNullable<typeof feature> => !!feature),
        };
      })
      .filter((group): group is NonNullable<typeof group> => !!group),
    products: returnedCatalogue.products
      ?.map((product, index) => {
        if (!product) return;
        return {
          id: product.fields.id,
          index,
          // Highlghted fields
          highlighted: product.sys.id === returnedCatalogue.highlightedProduct?.sys.id,
          highlightedColor: returnedCatalogue.highlightedColour,
          highlightedText: returnedCatalogue.highlightedText,
          highlightedBackground: returnedCatalogue.highlightedColourVariant?.toLowerCase(),
          // Icons and layout
          iconLayout: product.fields.iconLayout === 'Row' ? 'inline' : 'stacked',
          icons: product.fields.icons
            ?.map((icon) => getAssetIconData(icon))
            .filter((icon): icon is NonNullable<typeof icon> => !!icon),
          subcategoryHasCertifications: !!product.fields.documentIcons?.length,
          certifications: product.fields.documentIcons
            ?.map((cert) => getAssetIconData(cert))
            .filter(
              (certification): certification is NonNullable<typeof certification> =>
                !!certification,
            ),
          // Content
          title: product.fields.title,
          subtitle: product.fields.showCategoryName
            ? returnedCatalogue.Subcategory?.fields.name
            : undefined,
          description: product.fields.description,
          dividerEnabled: product.fields.dividerEnabled,
          dividerColor: product.fields.dividerColour,
          prices: product.fields.prices,
          ctas: product.fields.ctas
            ?.map((productCTA, i) => {
              if (!productCTA?.fields?.key) return null;

              const ctaFields = productCTA.fields;

              // Extract all TypeForm fields if destination is 'Form' and form reference exists
              const formContent: {
                formId?: number;
                formSlugId?: string;
                badge?: string;
                title?: string;
                description?: string;
                successTitle?: string;
                successDescription?: string;
                successCTA?: {
                  label?: string;
                  url?: string;
                  type?: 'Button' | 'Link';
                };
              } = {};

              if (ctaFields.destination === 'Form') {
                if (ctaFields.form?.fields?.marketoFormId) {
                  const formFields = ctaFields.form.fields;

                  formContent.formId = formFields.marketoFormId;
                  formContent.formSlugId = formFields.slugId;
                  formContent.badge = formFields.badge;
                  formContent.title = formFields.title;
                  formContent.description = formFields.description;
                  formContent.successTitle = formFields.successTitle;
                  formContent.successDescription = formFields.successDescription;

                  // Extract successCTA fields if available
                  if (formFields.successCTA?.fields) {
                    formContent.successCTA = {
                      label: formFields.successCTA.fields.label,
                      url: formFields.successCTA.fields.url,
                      type: formFields.successCTA.fields.type,
                    };
                  }

                  // Warn if slugId is missing (fallback to formId)
                  if (!formContent.formSlugId) {
                    console.warn(
                      `CTA "${ctaFields.label}" form has no slugId, using formId as fallback`,
                    );
                    formContent.formSlugId = formContent.formId.toString();
                  }
                } else {
                  console.warn(
                    `CTA "${ctaFields.label}" has destination 'Form' but no form reference or marketoFormId`,
                  );
                }
              }

              return {
                key: ctaFields.key as string,
                label: ctaFields.label,
                href: ctaFields.url,
                newWindow: ctaFields.newWindow || false,
                variant: (i <= 0 ? 'primary' : 'secondary') as 'primary' | 'secondary',
                destination: ctaFields.destination,
                productId: product.fields.id,
                ...formContent,
              };
            })
            .filter((productCTA): productCTA is NonNullable<typeof productCTA> => !!productCTA),
          features: product.fields.features
            ?.map((productFeature) => {
              if (!productFeature?.fields?.feature?.fields) return null;
              return {
                feature: productFeature.fields.feature.fields,
                included: productFeature.fields.included,
                customMessage: productFeature.fields.customMessage,
                showOnProductCard: productFeature.fields.showOnProductCard,
              };
            })
            .filter((feature): feature is NonNullable<typeof feature> => !!feature),
          // Additional Donation Info
          additionalDonationInfoText: product.fields.additionalDonationInfoText,
          additionalDonationInfoIcon:
            getAssetIconData(product.fields.additionalDonationInfoIcon)?.src ?? null,
        };
      })
      .filter((product) => product !== undefined),
  };
};

const fetchProductCatalogueImpl = async (subcategorySlug: string, preview: boolean = false) => {
  try {
    // First, get the subcategory by slug to get its ID
    const subcategory = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCategorySkeleton>({
      content_type: 'product-category',
      'fields.slug': subcategorySlug,
      limit: 1,
    });

    if (!subcategory || subcategory.total === 0) {
      console.log(`No subcategory found with slug: ${subcategorySlug}`);
      return null;
    }

    const subcategoryId = subcategory.items[0].sys.id;

    // Now get the product catalogue filtered by subcategory ID
    const productCatalogue = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCatalogueSkeleton>({
      content_type: 'productCatalogue',
      include: 10,
      'fields.Subcategory.sys.id': subcategoryId,
      limit: 1,
    });

    if (!productCatalogue || productCatalogue.total === 0) {
      console.log(`No product catalogue found for subcategory: ${subcategorySlug}`);
      return null;
    }

    const returnedCatalogue = productCatalogue.items[0].fields;

    return transformProductCatalogueFields(returnedCatalogue);
  } catch (error) {
    console.error('Error fetching product catalogue:', error);
    return null;
  }
};

const fetchProductCatalogueByIdImpl = async (id: string, preview: boolean = false) => {
  try {
    const productCatalogue = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCatalogueSkeleton>({
      content_type: 'productCatalogue',
      'sys.id': id,
      include: 10,
      limit: 1,
    });

    if (!productCatalogue || productCatalogue.total === 0) {
      console.log(`No product catalogue found for id: ${id}`);
      return null;
    }

    const returnedCatalogue = productCatalogue.items[0].fields;

    return transformProductCatalogueFields(returnedCatalogue);
  } catch (error) {
    console.error('Error fetching product catalogue by id:', error);
    return null;
  }
};

export const getProductCatalogue = unstable_cache(
  cache(fetchProductCatalogueImpl),
  ['product-catalogue'],
  {
    tags: ['product-catalogue', 'pricing'],
    revalidate: REVALIDATION_TIME,
  },
);

export const getProductCatalogueById = unstable_cache(
  cache(fetchProductCatalogueByIdImpl),
  ['product-catalogue-by-id'],
  {
    tags: ['product-catalogue', 'pricing'],
    revalidate: REVALIDATION_TIME,
  },
);

export type ProductCatalogue = Awaited<ReturnType<typeof getProductCatalogue>>;
